// "This credential is gone" — the one failure the whole SPA has to agree on.
//
// A dashboard session can die mid-use: the httpOnly `dz_session` cookie expires,
// an admin revokes the PAT, the session row is deleted. The app is mounted, the
// user is reading a panel, and from that instant EVERY `/v1` call 401s. Before
// this latch each panel just painted its own error and TanStack re-fired the same
// doomed request — nothing routed back to <Login/> short of a manual reload
// (the `<App/>` gate only ever read `/v1/me`, which by then was cached and never
// re-asked).
//
// A 401 is safe to read that literally because the API is precise about which
// failure it is: a missing / malformed / unknown credential is 401 and NOTHING
// else is. A refused `X-Dz-Account` tenant claim and a missing scope both answer
// 403 — "the credential is fine, the tenant claim is not"
// (`apps/api/src/middleware/auth.ts`). So a 401 from ANY endpoint means exactly
// one thing, and the only honest response is the sign-in screen.
//
// It LATCHES. Re-probing `/v1/me` with the same dead credential just 401s again,
// so there is nothing to recover to in-page; every sign-in leg (GitHub OAuth,
// org SSO, email) is a full-page navigation, which discards this module along
// with the rest of the SPA. `clearSessionExpired` exists for tests and for a
// future in-place re-auth — not as a "maybe it healed" retry.

import { createSignal } from 'solid-js';

const [sessionExpired, setSessionExpired] = createSignal(false);

/**
 * Latch the session as dead. THREE callers, and that is the whole list:
 * `apiFetch` (`lib/api.ts`), the one client every JSON `/v1` call goes through;
 * `attemptSse` (`lib/sse-subscribe.ts`), the one loop all three live dials go
 * through; and `fetchSessionJsonl` (`lib/auditExport.ts`), the one `.jsonl`
 * download. The last two are raw `fetch` — SSE so the `dev_pat_` bearer can ride
 * along where native EventSource cannot, the export because its body is not
 * JSON — so they never reach `apiFetch`. One seam per transport, not one per view.
 * `apiFetch` skips the latch for a call made with `credential: 'operator'`: that
 * bearer is a short-lived operator credential, so its 401 is not the session's.
 */
export function markSessionExpired(): void {
  setSessionExpired(true);
}

/** Release the latch. Tests, and a future in-place re-auth; not a retry hook. */
export function clearSessionExpired(): void {
  setSessionExpired(false);
}

/**
 * True once any `/v1` call has been refused for want of a credential. Read by
 * `<App/>`'s gate (via `resolveAppGate`), which then renders <Login/> — even
 * when a previously-fetched `me` is still sitting in the query cache.
 */
export { sessionExpired };
